import React, { useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function ApiPlayground({ apiKey, onUsageUpdate }) {
  const [mode, setMode] = useState('screenshot'); // 'screenshot' or 'pdf'
  const [url, setUrl] = useState('https://example.com');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Screenshot options
  const [format, setFormat] = useState('png');
  const [fullPage, setFullPage] = useState(false);
  const [quality, setQuality] = useState(80);
  const [blockAds, setBlockAds] = useState(false);
  const [blockCookieBanners, setBlockCookieBanners] = useState(false);

  // PDF options
  const [pdfFormat, setPdfFormat] = useState('A4');
  const [landscape, setLandscape] = useState(false);
  const [printBackground, setPrintBackground] = useState(true);

  const handleGenerate = async () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }

    if (!apiKey) {
      toast.error('Please create an API key first');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const endpoint = mode === 'screenshot' ? '/screenshot' : '/pdf';

      const requestData = mode === 'screenshot'
        ? {
            url,
            format,
            fullPage,
            quality: format === 'jpeg' || format === 'webp' ? parseInt(quality) : undefined,
            blockAds,
            blockCookieBanners
          }
        : {
            url,
            format: pdfFormat,
            landscape,
            printBackground
          };

      const response = await api.post(endpoint, requestData, {
        headers: {
          'X-API-Key': apiKey
        },
        responseType: 'arraybuffer'
      });

      // Convert to blob and create preview URL
      const blob = new Blob([response.data], {
        type: mode === 'screenshot'
          ? `image/${format}`
          : 'application/pdf'
      });

      const previewUrl = URL.createObjectURL(blob);

      setResult({
        blob,
        previewUrl,
        size: blob.size,
        type: mode,
        format: mode === 'screenshot' ? format : 'pdf'
      });

      toast.success(`${mode === 'screenshot' ? 'Screenshot' : 'PDF'} generated successfully!`);

      // Trigger usage update callback after a short delay
      // This allows the database to finish incrementing the usage counter
      if (onUsageUpdate) {
        setTimeout(() => {
          onUsageUpdate();
        }, 500);
      }
    } catch (error) {
      console.error('Generation error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;

    const extension = result.type === 'screenshot' ? result.format : 'pdf';
    const filename = `${mode}-${Date.now()}.${extension}`;

    const link = document.createElement('a');
    link.href = result.previewUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Download started!');
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Try It Out</h2>
          <p className="text-sm text-gray-600 mt-1">
            Test the API without writing code
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setMode('screenshot')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'screenshot'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📸 Screenshot
          </button>
          <button
            onClick={() => setMode('pdf')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'pdf'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📄 PDF
          </button>
        </div>
      </div>

      {/* URL Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL
        </label>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* Options */}
      <div className="mb-6">
        {mode === 'screenshot' ? (
          <>
            {/* Format & Quality Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Format
                </label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="png">PNG</option>
                  <option value="jpeg">JPEG</option>
                  <option value="webp">WebP</option>
                </select>
              </div>

              {/* Quality (for JPEG/WebP) */}
              {(format === 'jpeg' || format === 'webp') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quality ({quality}%)
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={quality}
                    onChange={(e) => setQuality(e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                  />
                </div>
              )}
            </div>

            {/* Options Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Options</h4>
              <div className="space-y-3">
                {/* Full Page */}
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    id="fullPage"
                    checked={fullPage}
                    onChange={(e) => setFullPage(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
                    Full Page Screenshot
                  </span>
                </label>

                {/* Block Ads */}
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    id="blockAds"
                    checked={blockAds}
                    onChange={(e) => setBlockAds(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
                    Block Ads
                  </span>
                </label>

                {/* Block Cookie Banners */}
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    id="blockCookieBanners"
                    checked={blockCookieBanners}
                    onChange={(e) => setBlockCookieBanners(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
                    Block Cookie Banners
                  </span>
                </label>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* PDF Page Size */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Page Size
              </label>
              <select
                value={pdfFormat}
                onChange={(e) => setPdfFormat(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="A4">A4</option>
                <option value="Letter">Letter</option>
                <option value="Legal">Legal</option>
                <option value="Tabloid">Tabloid</option>
              </select>
            </div>

            {/* PDF Options Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Options</h4>
              <div className="space-y-3">
                {/* Landscape */}
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    id="landscape"
                    checked={landscape}
                    onChange={(e) => setLandscape(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
                    Landscape Orientation
                  </span>
                </label>

                {/* Print Background */}
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    id="printBackground"
                    checked={printBackground}
                    onChange={(e) => setPrintBackground(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                  />
                  <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
                    Print Background Graphics
                  </span>
                </label>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={loading || !apiKey}
        className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
          loading || !apiKey
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-indigo-600 text-white hover:bg-indigo-700'
        }`}
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Generating...
          </span>
        ) : (
          `🚀 Generate ${mode === 'screenshot' ? 'Screenshot' : 'PDF'}`
        )}
      </button>

      {!apiKey && (
        <p className="text-sm text-red-600 mt-2 text-center">
          Please create an API key first to use this feature
        </p>
      )}

      {/* Result Preview */}
      {result && (
        <div className="mt-6 border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Result
              </h3>
              <p className="text-sm text-gray-600">
                {formatBytes(result.size)} • {result.format.toUpperCase()}
              </p>
            </div>
            <button
              onClick={handleDownload}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
          </div>

          {/* Preview */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            {result.type === 'screenshot' ? (
              <img
                src={result.previewUrl}
                alt="Screenshot preview"
                className="max-w-full h-auto rounded shadow-lg"
              />
            ) : (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-600 mb-4">PDF generated successfully!</p>
                <a
                  href={result.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-500 font-medium"
                >
                  Open PDF in new tab →
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
